import React, { useState, useRef, useEffect } from "react";
import {
  TextField, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox,
  RadioGroup, Radio, FormLabel, FormGroup, FormHelperText, Button, Box, Typography, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Popover, Autocomplete
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import dayjs from "dayjs";

// Custom date picker with month + year dropdowns (no extra dependency).
// Stores/returns the value as a 'YYYY-MM-DD' string, same as a native date input.
function MonthYearDatePicker(props) {
  var E = React.createElement;
  var value = props.value || "";
  var st = useState(null); var anchor = st[0], setAnchor = st[1];
  var initial = value ? dayjs(value) : dayjs();
  var ys = useState(initial.year()); var year = ys[0], setYear = ys[1];
  var ms = useState(initial.month()); var month = ms[0], setMonth = ms[1];

  function open(e) {
    var d = value ? dayjs(value) : dayjs();
    setYear(d.year()); setMonth(d.month()); setAnchor(e.currentTarget);
  }
  function close() { setAnchor(null); }
  function pick(day) { props.onChange(dayjs(new Date(year, month, day)).format("YYYY-MM-DD")); close(); }

  var monthsList = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  var weekdays = ["Su","Mo","Tu","We","Th","Fr","Sa"];
  var start = dayjs(new Date(year, month, 1));
  var daysInMonth = start.daysInMonth();
  var firstWeekday = start.day();
  var nowY = dayjs().year();
  var years = []; for (var y = nowY + 10; y >= 1940; y--) { years.push(y); }
  var sel = value ? dayjs(value) : null;

  var cells = [];
  var i;
  for (i = 0; i < firstWeekday; i++) { cells.push(E(Box, {key: "e" + i})); }
  for (i = 1; i <= daysInMonth; i++) {
    (function (dnum) {
      var isSel = sel && sel.date() === dnum && sel.month() === month && sel.year() === year;
      cells.push(E(Box, {key: "d" + dnum, sx: {display: "flex", justifyContent: "center"}},
        E(Button, {size: "small", onClick: function () { pick(dnum); }, variant: isSel ? "contained" : "text",
          sx: {minWidth: 0, width: 32, height: 32, p: 0, borderRadius: "50%"}}, String(dnum))));
    })(i);
  }

  return E(Box, null,
    E(TextField, {fullWidth: true, size: "small", value: value, placeholder: "Select a date",
      onClick: props.readOnly ? undefined : open, disabled: props.readOnly,
      InputProps: {readOnly: true}, inputProps: {style: {cursor: props.readOnly ? "default" : "pointer"}}}),
    E(Popover, {open: !!anchor, anchorEl: anchor, onClose: close,
      anchorOrigin: {vertical: "bottom", horizontal: "left"}},
      E(Box, {sx: {p: 2, width: 300}},
        E(Box, {sx: {display: "flex", gap: 1, mb: 1.5}},
          E(Select, {size: "small", value: month, onChange: function (e) { setMonth(e.target.value); }, sx: {flex: 1}},
            monthsList.map(function (m, idx) { return E(MenuItem, {key: idx, value: idx}, m); })),
          E(Select, {size: "small", value: year, onChange: function (e) { setYear(e.target.value); }, sx: {width: 100}},
            years.map(function (yy) { return E(MenuItem, {key: yy, value: yy}, String(yy)); }))),
        E(Box, {sx: {display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, textAlign: "center", mb: 0.5}},
          weekdays.map(function (w) { return E(Typography, {key: w, variant: "caption", color: "text.secondary"}, w); })),
        E(Box, {sx: {display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5}}, cells),
        E(Box, {sx: {display: "flex", justifyContent: "space-between", mt: 1}},
          E(Button, {size: "small", onClick: function () { props.onChange(""); close(); }}, "Clear"),
          E(Button, {size: "small", onClick: function () { var t = dayjs(); setYear(t.year()); setMonth(t.month()); }}, "Today")))));
}

// Fallback list, used only if /api/public/countries cannot be reached.
// The live list lives in the `countries` table and is edited from
// Admin -> Countries. See migration 010_countries.sql.
var FALLBACK_COUNTRIES = [
  {name:"Afghanistan", iso2:"AF", dial_code:"+93"},
  {name:"Australia", iso2:"AU", dial_code:"+61"},
  {name:"Bangladesh", iso2:"BD", dial_code:"+880"},
  {name:"Brazil", iso2:"BR", dial_code:"+55"},
  {name:"Canada", iso2:"CA", dial_code:"+1"},
  {name:"China", iso2:"CN", dial_code:"+86"},
  {name:"France", iso2:"FR", dial_code:"+33"},
  {name:"Germany", iso2:"DE", dial_code:"+49"},
  {name:"India", iso2:"IN", dial_code:"+91"},
  {name:"Indonesia", iso2:"ID", dial_code:"+62"},
  {name:"Kenya", iso2:"KE", dial_code:"+254"},
  {name:"Nepal", iso2:"NP", dial_code:"+977"},
  {name:"Nigeria", iso2:"NG", dial_code:"+234"},
  {name:"Pakistan", iso2:"PK", dial_code:"+92"},
  {name:"Philippines", iso2:"PH", dial_code:"+63"},
  {name:"South Africa", iso2:"ZA", dial_code:"+27"},
  {name:"Sri Lanka", iso2:"LK", dial_code:"+94"},
  {name:"United Kingdom", iso2:"GB", dial_code:"+44"},
  {name:"United States", iso2:"US", dial_code:"+1"}
];

// Fetched once per page load and shared by every country and phone field.
var countryPromise = null;
function loadCountries() {
  if (countryPromise) return countryPromise;
  countryPromise = fetch("/api/public/countries")
    .then(function(r) { return r.json(); })
    .then(function(j) {
      var list = (j && j.data) || [];
      return list.length ? list : FALLBACK_COUNTRIES;
    })
    .catch(function() { return FALLBACK_COUNTRIES; });
  return countryPromise;
}

function useCountries() {
  var st = useState(null); var list = st[0], setList = st[1];
  useEffect(function() {
    var alive = true;
    loadCountries().then(function(l) { if (alive) setList(l); });
    return function() { alive = false; };
  }, []);
  return list;
}

function extractYouTubeId(url) {
  if (!url) return null;
  var m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|.*[?&]v=))([^&#?]+)/);
  return m ? m[1] : null;
}

function countWords(text) {
  if (!text || typeof text !== 'string') return 0;
  return text.trim().split(/\s+/).filter(function(w) { return w.length > 0; }).length;
}

function DescriptionText(props) {
  if (!props.text) return null;
  var E = React.createElement;
  if (/<[a-z][\s\S]*>/i.test(props.text)) {
    return E(Typography, {variant:"body2", color:"text.secondary", sx: Object.assign({mb:1}, props.sx || {}),
      dangerouslySetInnerHTML: {__html: props.text}});
  }
  return E(Typography, {variant:"body2", color:"text.secondary",
    sx: Object.assign({mb:1, whiteSpace:"pre-wrap"}, props.sx || {})}, props.text);
}

function FieldWrapper(props) {
  var E = React.createElement;
  var field = props.field;
  return E(Box, {sx:{p:2, border:'1px solid', borderColor:'divider', borderRadius:1, bgcolor:'white'}},
    E(Typography, {variant:"subtitle1", sx:{fontWeight:600, mb:0.5, color:'text.primary'}},
      field.label, field.required ? E("span", {style:{color:'#c0392b',marginLeft:4}}, "*") : null),
    field.help_text ? E(DescriptionText, {text: field.help_text, sx:{mb:1.5, fontSize:'0.85rem'}}) : null,
    props.children,
    props.error ? E(FormHelperText, {error:true}, props.error) : null);
}

function TermsDialog(props) {
  var contentRef = useRef(null);
  var st = useState(!props.mustScroll); var scrolled = st[0]; var setScrolled = st[1];
  function onScroll() {
    if (!contentRef.current || !props.mustScroll) return;
    var el = contentRef.current;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) setScrolled(true);
  }
  useEffect(function() { if (props.open) setScrolled(!props.mustScroll); }, [props.open, props.mustScroll]);
  var E = React.createElement;
  return E(Dialog, {open:props.open, onClose:props.onClose, maxWidth:"md", fullWidth:true},
    E(DialogTitle, null, props.title || "Terms & Conditions"),
    E(DialogContent, {dividers:true, ref:contentRef, onScroll:onScroll, sx:{maxHeight:400,overflowY:"auto"}},
      props.url && !props.text
        ? E("iframe", {src:props.url, title:props.title, style:{width:"100%",height:360,border:"none"}})
        : E("div", {dangerouslySetInnerHTML:{__html: props.text || "No policy text."}, style:{fontSize:14}})),
    E(DialogActions, null,
      E(Button, {onClick:props.onClose}, "Close"),
      E(Button, {variant:"contained", onClick:props.onAccept, disabled:!scrolled}, "I have read and agree")));
}

function TermsField(props) {
  var ds = useState(false); var open = ds[0]; var setOpen = ds[1];
  var field = props.field; var v = field.validation || {};
  var requireOpen = v.require_open !== false;
  var E = React.createElement;
  return E(Box, null,
    field.help_text ? E(DescriptionText, {text: field.help_text, sx:{mb:1, fontSize:"0.85rem"}}) : null,
    E(FormControlLabel, {
      control: E(Checkbox, {
        checked:!!props.value,
        disabled: props.readOnly || (requireOpen && !props.value),
        onChange:function(e){
          if (requireOpen) { if(!e.target.checked) props.onChange(field.id, false); }
          else { props.onChange(field.id, e.target.checked); }
        }}),
      label: E(Typography, {variant:"body2"},
        "I agree to the ",
        E("span", {style:{color:"#1a5276",cursor:"pointer",textDecoration:"underline"},
          onClick:function(e){e.preventDefault();setOpen(true);}}, v.policy_title || field.label),
        field.required ? " *" : "")}),
    props.error ? E(FormHelperText, {error:true,sx:{ml:4}}, props.error) : null,
    E(TermsDialog, {open:open, onClose:function(){setOpen(false);},
      onAccept:function(){props.onChange(field.id, true); setOpen(false);},
      title:v.policy_title||field.label, text:v.policy_text, url:v.policy_url,
      mustScroll: requireOpen && (v.must_scroll!==false)}));
}

function PhoneField(props) {
  var field = props.field; var value = props.value || ""; var E = React.createElement;
  var countries = useCountries();
  var parts = value.split("-");
  var countryCode = parts.length > 1 ? parts[0] : "+91";
  var phoneNum = parts.length > 1 ? parts[1] : value;

  // One entry per distinct dialling code, labelled with the countries sharing it.
  var codes = [];
  var seen = {};
  (countries || FALLBACK_COUNTRIES).forEach(function(c) {
    if (!c.dial_code) return;
    if (seen[c.dial_code] !== undefined) {
      var at = seen[c.dial_code];
      if (codes[at].names.length < 3) codes[at].names.push(c.iso2 || c.name);
      return;
    }
    seen[c.dial_code] = codes.length;
    codes.push({code: c.dial_code, names: [c.iso2 || c.name]});
  });
  codes.sort(function(a, b) { return a.names[0].localeCompare(b.names[0]); });
  // The stored code may no longer be in the list; keep it selectable regardless.
  if (countryCode && seen[countryCode] === undefined) codes.unshift({code: countryCode, names: [countryCode]});

  return E(Box, {sx:{display:"flex", gap:1}},
    E(FormControl, {sx:{minWidth:140}},
      E(InputLabel, {size:"small"}, "Code"),
      E(Select, {value:countryCode, label:"Code", size:"small",
        MenuProps:{PaperProps:{style:{maxHeight:320}}},
        onChange:function(e){props.onChange(field.id, e.target.value + "-" + phoneNum);}, disabled:props.readOnly},
        codes.map(function(c) {
          return E(MenuItem, {key:c.code + "|" + c.names[0], value:c.code}, c.names.join("/") + " " + c.code);
        }))),
    E(TextField, {fullWidth:true, size:"small", placeholder:"9876543210",
      value:phoneNum, onChange:function(e){ props.onChange(field.id, countryCode + "-" + e.target.value.replace(/[^0-9]/g, "")); },
      disabled:props.readOnly, inputProps:{inputMode:"numeric", pattern:"[0-9]*"}}));
}

function TextareaWithWordCount(props) {
  var E = React.createElement;
  var wc = countWords(props.value);
  var minW = (props.validation && props.validation.min_words) || 0;
  var maxW = (props.validation && props.validation.max_words) || 0;
  var over = maxW > 0 && wc > maxW;
  var under = minW > 0 && wc < minW && wc > 0;
  return E(Box, null,
    E(TextField, {fullWidth:true, multiline:true, rows:4, placeholder:props.placeholder,
      value:props.value, onChange:props.onChange, disabled:props.readOnly, error:over||under}),
    E(Box, {sx:{display:'flex', justifyContent:'space-between', mt:0.5}},
      (minW > 0 || maxW > 0) ? E(Typography, {variant:"caption", color:over||under?"error":"text.secondary"},
        (minW > 0 ? "Min " + minW + " words" : ""), (minW > 0 && maxW > 0 ? " \u2022 " : ""),
        (maxW > 0 ? "Max " + maxW + " words" : "")) : E("span"),
      E(Chip, {label:wc + " words", size:"small", variant:"outlined",
        color:over?"error":under?"warning":"default", sx:{height:20, fontSize:11}})));
}

function UrlField(props) {
  var E = React.createElement;
  var field = props.field; var value = props.value || '';
  var us = useState(false); var invalid = us[0]; var setInvalid = us[1];
  function validate(v) {
    if (!v) { setInvalid(false); return; }
    try { new URL(v); setInvalid(false); } catch(e6) { setInvalid(true); }
  }
  return E(FieldWrapper, {field:field, error:props.error || (invalid ? 'Please enter a valid URL (e.g. https://example.com)' : null)},
    E(TextField, {fullWidth:true, size:"small", placeholder:field.placeholder || "https://",
      value:value, disabled:props.readOnly,
      onChange:function(e) { props.onChange(field.id, e.target.value); validate(e.target.value); },
      onBlur:function() { validate(value); },
      error:invalid}));
}

function VideoSelector(props) {
  var E = React.createElement;
  var field = props.field; var value = props.value || '';
  var v = field.validation || {};
  var urls = (v.video_urls || []).filter(function(u) { return u && u.trim(); });
  var singleUrl = v.video_url || '';

  // Single video mode
  if (urls.length === 0 && singleUrl) {
    var vid = extractYouTubeId(singleUrl);
    return E(FieldWrapper, {field:field, error:props.error}, E(Box, null,
      vid ? E(Box, {sx:{position:"relative",paddingBottom:"56.25%",height:0,mb:2,borderRadius:2,overflow:"hidden"}},
        E("iframe",{src:"https://www.youtube-nocookie.com/embed/"+vid, title:field.label, style:{position:"absolute",top:0,left:0,width:"100%",height:"100%",border:0}, allow:"accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture", allowFullScreen:true})) : null,
      v.has_response ? E(TextField,{fullWidth:true,size:"small",placeholder:"Your response",multiline:true,rows:3,value:value,onChange:function(e){props.onChange(field.id,e.target.value);},disabled:props.readOnly}) : null));
  }

  // Multi-video mode: user selects one
  if (urls.length > 0) {
    // Parse value: could be "VIDEO_INDEX|response_text"
    var parts = (value || '').split('|');
    var selectedIdx = parts[0] ? parseInt(parts[0]) : -1;
    var responseText = parts.length > 1 ? parts.slice(1).join('|') : '';
    var selectedUrl = (selectedIdx >= 0 && selectedIdx < urls.length) ? urls[selectedIdx] : '';
    var selectedVid = extractYouTubeId(selectedUrl);

    return E(FieldWrapper, {field:field, error:props.error}, E(Box, null,
      E(Typography, {variant:"body2", color:"text.secondary", sx:{mb:1}}, 'Select a video to respond to:'),
      E(FormControl, {fullWidth:true, size:"small", sx:{mb:2}},
        E(Select, {value: selectedIdx >= 0 ? selectedIdx : '', displayEmpty:true,
          onChange:function(e) {
            var idx = e.target.value;
            props.onChange(field.id, idx + '|' + responseText);
          }},
          E(MenuItem, {value:'', disabled:true}, E("em", null, "Choose a video...")),
          urls.map(function(u, i) {
            var title = v.video_titles && v.video_titles[i] ? v.video_titles[i] : 'Video ' + (i + 1);
            return E(MenuItem, {key:i, value:i}, title);
          }))),
      selectedVid ? E(Box, {sx:{position:"relative",paddingBottom:"56.25%",height:0,mb:2,borderRadius:2,overflow:"hidden"}},
        E("iframe",{src:"https://www.youtube-nocookie.com/embed/"+selectedVid, title:field.label, style:{position:"absolute",top:0,left:0,width:"100%",height:"100%",border:0}, allow:"accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture", allowFullScreen:true})) : null,
      v.has_response ? E(TextField,{fullWidth:true,size:"small",placeholder:"Your response",multiline:true,rows:3,
        value:responseText, onChange:function(e) { props.onChange(field.id, selectedIdx + '|' + e.target.value); },
        disabled:props.readOnly}) : null));
  }

  // No videos configured
  return E(FieldWrapper, {field:field, error:props.error},
    E(Typography, {variant:"body2", color:"text.secondary"}, "No video configured for this field."));
}

function CountryField(props) {
  var field = props.field; var E = React.createElement;
  var countries = useCountries();
  var names = (countries || FALLBACK_COUNTRIES).map(function(c) { return c.name; });
  var value = props.value || "";
  // A country recorded before it was removed from the list still has to display.
  if (value && names.indexOf(value) === -1) names = [value].concat(names);
  return E(Autocomplete, {
    options: names,
    value: value || null,
    disabled: props.readOnly,
    loading: countries === null,
    autoHighlight: true,
    onChange: function(_, v) { props.onChange(field.id, v || ""); },
    isOptionEqualToValue: function(o, v) { return o === v; },
    renderInput: function(p) {
      return E(TextField, Object.assign({}, p, {
        size: "small",
        placeholder: field.placeholder || "Start typing a country",
      }));
    }
  });
}

function RenderField(props) {
  var field = props.field;
  var value = (props.values && props.values[field.id]) || "";
  var error = props.errors && props.errors[field.id];
  var E = React.createElement;
  switch (field.type) {
    case "text": case "email": case "number":
      return E(FieldWrapper, {field:field, error:error},
        E(TextField, {fullWidth:true, size:"small", type:field.type==="number"?"number":"text",
          placeholder:field.placeholder, value:value,
          onChange:function(e){props.onChange(field.id,e.target.value);}, disabled:props.readOnly}));
    case "url":
      return E(UrlField, {field:field, value:value, onChange:props.onChange, error:error, readOnly:props.readOnly});
    case "phone":
      return E(FieldWrapper, {field:field, error:error},
        E(PhoneField, {field:field, value:value, onChange:props.onChange, readOnly:props.readOnly}));
    case "textarea":
      return E(FieldWrapper, {field:field, error:error},
        E(TextareaWithWordCount, {value:value, placeholder:field.placeholder,
          validation:field.validation, readOnly:props.readOnly,
          onChange:function(e){props.onChange(field.id,e.target.value);}}));
    case "date":
      return E(FieldWrapper, {field:field, error:error},
        E(MonthYearDatePicker, {value:value, readOnly:props.readOnly,
          onChange:function(v){props.onChange(field.id, v);}}));
    case "country":
      return E(FieldWrapper, {field:field, error:error},
        E(CountryField, {field:field, value:value, onChange:props.onChange, readOnly:props.readOnly}));
    case "dropdown":
      var opts = field.options || [];
      return E(FieldWrapper, {field:field, error:error},
        E(FormControl, {fullWidth:true, size:"small"},
          E(Select, {value:value, displayEmpty:true, onChange:function(e){props.onChange(field.id,e.target.value);}},
            E(MenuItem, {value:"", disabled:true}, E("em", null, "Select...")),
            opts.map(function(o){return E(MenuItem,{key:o,value:o},o);}))));
    case "radio":
      return E(FieldWrapper, {field:field, error:error},
        E(RadioGroup, {value:value, onChange:function(e){props.onChange(field.id,e.target.value);}},
          (field.options||[]).map(function(o){return E(FormControlLabel,{key:o,value:o,control:E(Radio),label:o});})));
    case "checkbox":
      var checked = Array.isArray(value) ? value : [];
      return E(FieldWrapper, {field:field, error:error},
        E(FormGroup, null, (field.options||[]).map(function(o){
          return E(FormControlLabel,{key:o,label:o,control:E(Checkbox,{checked:checked.indexOf(o)>=0,
            onChange:function(e){props.onChange(field.id, e.target.checked ? checked.concat([o]) : checked.filter(function(x){return x!==o;}));}})});})));
    case "file_upload":
      return E(FieldWrapper, {field:field, error:error},
        E(Box, null,
          E(Button, {variant:"outlined", component:"label", startIcon:E(CloudUpload), disabled:props.readOnly, size:"small"},
            "Choose File", E("input", {type:"file", hidden:true, onChange:function(e){if(props.onFileChange)props.onFileChange(field.id,e.target.files[0]);}})),
          value ? E(Typography,{variant:"body2",sx:{mt:0.5,color:'success.main'}}, typeof value==="string"?value:value.name) : null));
    case "video":
      return E(VideoSelector, {field:field, value:value, onChange:props.onChange, error:error, readOnly:props.readOnly});
    case "terms_acceptance":
      return E(TermsField, {field:field, value:value, onChange:props.onChange, error:error, readOnly:props.readOnly});
    default:
      return E(FieldWrapper, {field:field, error:error},
        E(TextField, {fullWidth:true, size:"small", value:value,
          onChange:function(e){props.onChange(field.id,e.target.value);}, disabled:props.readOnly}));
  }
}

export default function FormRenderer(props) {
  if (!props.schema) return null;
  var E = React.createElement;
  var fields = (props.schema.fields||[]).slice().sort(function(a,b){return a.order-b.order;});
  var sections = (props.schema.sections||[]).slice().sort(function(a,b){return a.order-b.order;});
  var unsectioned = fields.filter(function(f){return !f.section_id && (!f.scholar_only||props.scholarOnly);});
  return E(Box, null,
    unsectioned.map(function(f){return E(Box,{key:f.id,sx:{mb:2.5}}, E(RenderField,{field:f,values:props.values||{},onChange:props.onChange||function(){},onFileChange:props.onFileChange,errors:props.errors,readOnly:props.readOnly}));}),
    sections.map(function(sec){
      var sf = fields.filter(function(f){return f.section_id===sec.id && (!f.scholar_only||props.scholarOnly);});
      if (!sf.length) return null;
      return E(Paper,{key:sec.id,variant:"outlined",sx:{p:3,mb:3}},
        E(Typography,{variant:"h6",sx:{mb:0.5,color:"primary.main"}},sec.title),
        sec.description ? E(DescriptionText, {text:sec.description, sx:{mb:2}}) : null,
        sf.map(function(f){return E(Box,{key:f.id,sx:{mb:2.5}}, E(RenderField,{field:f,values:props.values||{},onChange:props.onChange||function(){},onFileChange:props.onFileChange,errors:props.errors,readOnly:props.readOnly}));}));}));
}

export function MultiStepFormRenderer(props) {
  if (!props.schema) return null;
  var E = React.createElement;
  var fields = (props.schema.fields||[]).slice().sort(function(a,b){return a.order-b.order;});
  var sections = (props.schema.sections||[]).slice().sort(function(a,b){return a.order-b.order;});
  if (sections.length===0) {
    return E(Box, null, fields.filter(function(f){return !f.scholar_only||props.scholarOnly;}).map(function(f){
      return E(Box,{key:f.id,sx:{mb:2.5}}, E(RenderField,{field:f,values:props.values||{},onChange:props.onChange||function(){},onFileChange:props.onFileChange,errors:props.errors,readOnly:props.readOnly}));}));
  }
  var section = sections[props.currentStep] || sections[0];
  var sectionDesc = section && section.description ? E(DescriptionText, {text:section.description, sx:{mb:2}}) : null;
  var sf = fields.filter(function(f){return f.section_id===section.id && (!f.scholar_only||props.scholarOnly);});
  return E(Box, null, sectionDesc,
    sf.map(function(f){ return E(Box,{key:f.id,sx:{mb:2.5}}, E(RenderField,{field:f,values:props.values||{},onChange:props.onChange||function(){},onFileChange:props.onFileChange,errors:props.errors,readOnly:props.readOnly}));}));
}

export var SinglePageFormRenderer = FormRenderer;
